/**
 * AdminSupportDashboard
 * ----------------------
 * Two-panel real-time support dashboard.
 *
 * Panel A — Dashboard socket (?support=true&agentId=X, NO sessionId)
 *   Connects once on mount and listens for all supportChannel events.
 *   Builds the live pending sessions list in memory.
 *
 * Panel B — Chat window (opens when agent clicks "Join")
 *   Opens a second socket (?sessionId=xxx&support=true&agentId=X).
 *   Lets the agent send support_message frames and read user replies.
 *   Closing removes the session from the pending list.
 */

import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography, Chip } from '@mui/material';
import { observer } from 'mobx-react-lite';
import authStore from '../../store/authStore';
import SupportSessionList, { type SupportRequest } from './components/SupportSessionList';
import SupportChatWindow from './components/SupportChatWindow';

const WS_URL = process.env.REACT_APP_MAIN_BE_WS as string;

const AdminSupportDashboard: React.FC = observer(() => {
  const [sessions, setSessions] = useState<SupportRequest[]>([]);
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);
  const [dashboardStatus, setDashboardStatus] = useState<'connecting' | 'connected' | 'error' | 'closed'>('connecting');
  const dashboardWsRef = useRef<WebSocket | null>(null);

  const agentId = authStore.user?.id ?? 0;

  // ----------------------------------------------------------
  // Dashboard socket — listens for supportChannel broadcasts
  // ----------------------------------------------------------
  useEffect(() => {
    if (!WS_URL) return;

    const url = `${WS_URL}?support=true&agentId=${encodeURIComponent(agentId)}`;
    const ws = new WebSocket(url);
    dashboardWsRef.current = ws;

    ws.onopen = () => setDashboardStatus('connected');
    ws.onerror = () => setDashboardStatus('error');
    ws.onclose = () => setDashboardStatus('closed');

    ws.onmessage = (event) => {
      try {
        const payload = JSON.parse(event.data);
        if (payload.type === 'support_request') {
          const request: SupportRequest = {
            sessionId: payload.sessionId,
            userId: payload.userId,
            question: payload.question,
            timestamp: payload.timestamp ?? Date.now(),
          };
          setSessions((prev) => {
            // Deduplicate by sessionId
            if (prev.some((s) => s.sessionId === request.sessionId)) return prev;
            return [request, ...prev];
          });
        }
      } catch {
        // ignore non-JSON frames
      }
    };

    return () => {
      ws.close();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentId]);

  // ----------------------------------------------------------
  // Join a session — opens the chat panel
  // ----------------------------------------------------------
  const handleJoin = (sessionId: string) => {
    setActiveSessionId(sessionId);
  };

  // ----------------------------------------------------------
  // Leave a session — close chat panel, remove from list
  // ----------------------------------------------------------
  const handleLeave = () => {
    setActiveSessionId(null);
    // Remove the session from the pending list once the agent leaves
    setSessions((prev) => prev.filter((s) => s.sessionId !== activeSessionId));
  };

  const statusColour: Record<typeof dashboardStatus, 'success' | 'warning' | 'error' | 'default'> = {
    connected: 'success',
    connecting: 'warning',
    error: 'error',
    closed: 'default',
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 128px)', p: 2, gap: 2 }}>
      {/* Title row */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Typography variant="h5" fontWeight={700}>
          Support Dashboard
        </Typography>
        <Chip
          size="small"
          label={dashboardStatus}
          color={statusColour[dashboardStatus]}
          variant="outlined"
        />
      </Box>

      {/* Main panels */}
      <Box sx={{ display: 'flex', flex: 1, gap: 2, minHeight: 0 }}>
        {/* Left panel — pending sessions list */}
        <Box sx={{ width: 300, flexShrink: 0 }}>
          <SupportSessionList
            sessions={sessions}
            activeSessionId={activeSessionId}
            onJoin={handleJoin}
          />
        </Box>

        {/* Right panel — active chat window or empty state */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {activeSessionId ? (
            <SupportChatWindow
              key={activeSessionId}
              sessionId={activeSessionId}
              agentId={agentId}
              wsUrl={WS_URL}
              onLeave={handleLeave}
            />
          ) : (
            <Box
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'text.secondary',
                border: '2px dashed',
                borderColor: 'divider',
                borderRadius: 2,
              }}
            >
              <Typography variant="body1" fontWeight={500}>
                Select a session to start chatting
              </Typography>
              <Typography variant="caption" sx={{ mt: 0.5 }}>
                Click "Join" on a pending request to open the chat window
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
});

export default AdminSupportDashboard;


